import { useState, useEffect } from "react";
import { Dialog, DialogContent, Box, IconButton } from "@mui/material";
import { X } from "@phosphor-icons/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Zoom } from "swiper/modules";
import { Swiper as SwiperType } from 'swiper';

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";

const swiperStyles = `
  .fullscreen-swiper .swiper-button-next::after,
  .fullscreen-swiper .swiper-button-prev::after {
    color: #cccccc !important;
    font-size: 2em !important; 
    transform: scale(0.6);
    font-weight: bold;
  }
  .fullscreen-swiper .swiper-button-next,
  .fullscreen-swiper .swiper-button-prev {
    top: 50% !important;
  }
  .fullscreen-swiper .swiper-button-next {
    right: 50px !important;
  }
  .fullscreen-swiper .swiper-button-prev {
    left: 50px !important;
  }
  .fullscreen-swiper .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
`;

interface FullscreenImageDialogProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  productName: string;
  selectedImage: number;
}

export default function FullscreenImageDialog({
  open,
  onClose,
  images,
  productName,
  selectedImage,
}: FullscreenImageDialogProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [currentIndex, setCurrentIndex] = useState(selectedImage);

  useEffect(() => {
    if (swiperInstance) {
      swiperInstance.slideTo(selectedImage, 0);
      setCurrentIndex(selectedImage);
    }
  }, [selectedImage, swiperInstance]);

  const handleZoomClick = (e: React.MouseEvent) => {
    if (!swiperInstance) return;
    
    // Toggle zoom - Swiper handles this automatically when zoom module is enabled
    // and the swiper-zoom-container has the click handler
    const zoom = swiperInstance.zoom;
    if (zoom && zoom.scale && zoom.scale > 1) {
      zoom.out();
    } else {
      // Let Swiper handle the zoom in automatically
      // The zoom module will handle the click event internally
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          overflow: "hidden",
        },
      }}
      sx={{
        zIndex: 2000,
      }}
    >
      <style>{swiperStyles}</style>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Fadeout overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "60px",
            background: "linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        />

        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "white",
            zIndex: 10,
          }}
        >
          <X size={24} />
        </IconButton>

        <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              color: "white",
              zIndex: 10,
              fontFamily: "inherit",
              fontSize: "16px",
              padding: "4px 12px",
              borderRadius: "16px",
            }}
          >
            {currentIndex + 1} / {images.length}
        </Box>

        {/* Swiper */}
        <Swiper
          onSwiper={setSwiperInstance}
          onSlideChange={(swiper: SwiperType) => setCurrentIndex(swiper.activeIndex)}
          spaceBetween={10}
          slidesPerView={1}
          navigation={true}
          zoom={{
            maxRatio: 3,
            minRatio: 1,
          }}
          modules={[Navigation, Zoom]}
          className="fullscreen-swiper"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {images.map((image, index) => (
            <SwiperSlide key={index}>
              <Box
                className="swiper-zoom-container"
                onClick={handleZoomClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  cursor: "zoom-in",
                  '&.swiper-slide-zoomed-in': {
                    cursor: 'zoom-out',
                  }
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`${productName} - Image ${index + 1}`}
                  sx={{
                    height: "825px",
                    width: "75%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </DialogContent>
    </Dialog>
  );
}














